import React, { useState, useEffect } from 'react';
import { ILobbyState } from '../../../../Core/State/ILobbyState';
import { IUserState } from '../../../../Core/State/IUserState';
import { IRoomState } from '../../../../Core/State/IRoomState';
import { Stack, Typography, Button, Box, Paper, Grid, Card, CardContent, CardActions, Tooltip } from '@mui/material';
import MobileRoomBar from '../../../RoomBar/MobileRoomBar';
import styles from '../../../../assets/css/components/Lobby.module.css';
import { AppSections } from '../../../../Core/State/AppSections';
import { t } from '../../../../i18n'; 
import bnbIcon from '/crypto/bnb.svg';
import busdIcon from '/crypto/busd.svg';
import daiIcon from '/crypto/dai.svg';
import dogeIcon from '/crypto/doge.svg';
import dotIcon from '/crypto/dot.svg';
import shibIcon from '/crypto/shib.svg';
import solIcon from '/crypto/sol.svg';
import solamaIcon from '/crypto/solama.svg';
import usdcIcon from '/crypto/usdc.svg';
import usdtIcon from '/crypto/usdt.svg';
import vlxIcon from '/crypto/vlx.svg';
import xmrIcon from '/crypto/xmr.svg';
import { IJoinedRoomState } from '../../../../Core/State/IJoinedRoomState';

interface MobileLobbyProps {
    userState: IUserState;
    lobbyState: ILobbyState;
    joinedRoomsState: IJoinedRoomState[];
    selectedRoomId: string | null;
    joinRoom: (roomId: string) => void;
    section: string;
    setSection: (section: string) => void;
    logout: () => void;
    userConnectedCount: number;
}

const MobileTournamentRoomsLobby: React.FC<MobileLobbyProps> = ({ 
    userState, 
    lobbyState, 
    joinedRoomsState, 
    selectedRoomId,
    joinRoom,
    section,
    setSection,
    logout,
    userConnectedCount
}) => {
    const [filteredRooms, setFilteredRooms] = useState<IRoomState[]>([]);

    useEffect(() => {
        // Filter rooms that belong to a tournament (assuming a tournamentId exists)
        const rooms = lobbyState.rooms.filter(room => 
            room.tournamentId // Filter by tournamentId presence
        );
        setFilteredRooms(rooms);
    }, [lobbyState]);

    const getCryptoIcon = (currency: string) => {
        switch(currency?.toLowerCase()) {
            case 'bnb': return bnbIcon;
            case 'busd': return busdIcon;
            case 'dai': return daiIcon;
            case 'doge': return dogeIcon;
            case 'dot': return dotIcon;
            case 'shib': return shibIcon;
            case 'sol': return solIcon;
            case 'solama': return solamaIcon;
            case 'usdc': return usdcIcon;
            case 'usdt': return usdtIcon;
            case 'vlx': return vlxIcon;
            case 'xmr': return xmrIcon;
            default: return null;
        }
    };

    const formatCurrencyValue = (value: number) => {
        if (value === undefined || value === null) return t('common:labels.na');
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: value % 1 === 0 ? 0 : 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const getGameTypeDisplay = (type: string) => {
        switch (type?.toLowerCase()) {
            case 'texas': return t('Tournament:gameTypes.holdem');
            case 'aof': return t('Tournament:gameTypes.aof');
            default: return type ? type.charAt(0).toUpperCase() + type.slice(1) : t('common:labels.na');
        }
    };

    const getStakesDisplay = (room: IRoomState) => {
        const stakesText = room.ante ? `${t('common:labels.ante')}: ${formatCurrencyValue(room.ante)}` : `${formatCurrencyValue(room.smallBlind)} / ${formatCurrencyValue(room.bigBlind)}`;
        return room.currencyType === 'fiat' ? `$${stakesText}` : stakesText;
    };

    const getBuyInDisplay = (room: IRoomState) => {
        let buyInText;
        if (room.type === 'aof') {
            buyInText = formatCurrencyValue(room.maxBuyIn);
        } else if (room.type === 'texas') {
            buyInText = `${formatCurrencyValue(room.minBuyIn)} / ${formatCurrencyValue(room.maxBuyIn)}`;
        } else {
            buyInText = t('common:labels.na');
        }
        return room.currencyType === 'fiat' ? `$${buyInText}` : buyInText;
    };

    const getCurrencyDisplay = (room: IRoomState) => {
        if (room.currencyType === 'fiat') return '$';
        const icon = getCryptoIcon(room.currency);
        return icon ? (
            <Tooltip title={room.currency} placement="top" arrow>
                <img src={icon} alt={room.currency} style={{ width: '16px', height: '16px' }} />
            </Tooltip>
        ) : room.currency;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 110px)', overflow: 'hidden', padding: '16px', backgroundColor: '#191A1F' }}>
            <MobileRoomBar 
                userConnectedCount={userConnectedCount}
            />
            <Paper elevation={0} sx={{ flexGrow: 1, overflowY: 'auto', backgroundColor: 'transparent', mt: 2, 
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(255,255,255,.3)',
                    borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    background: 'rgba(255,255,255,.5)',
                },
            }}>
                <Grid container spacing={2}>
                    {filteredRooms.map((room) => {
                        const { playerSatCount = 0, minPlayers = 0, maxPlayers = 0 } = room;
                        let playerStatusColor = '#179F55'; // Green: seats available
                        if (playerSatCount >= maxPlayers) {
                            playerStatusColor = '#FF004D'; // Red: full
                        } else if (playerSatCount < minPlayers) {
                            playerStatusColor = '#FFC100'; // Yellow: needs players
                        }
                        
                        return (
                            <Grid item xs={12} sm={6} md={4} key={room.roomId}>
                                <Card sx={{ backgroundColor: '#2a2b32', color: 'white', borderRadius: '8px' }}>
                                    <CardContent sx={{ paddingBottom: '8px' }}>
                                        <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {room.name}
                                        </Typography>
                                        <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 1, fontSize: '0.8rem', color: '#a0a0a0' }}>
                                            <Typography variant="body2">{getGameTypeDisplay(room.type)}</Typography>
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                {getCurrencyDisplay(room)}
                                                <Typography variant="body2">{getStakesDisplay(room)}</Typography>
                                            </Stack>
                                        </Stack>
                                        <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 0.5, fontSize: '0.8rem', color: '#a0a0a0' }}>
                                            <Typography variant="body2">{t('common:labels.buyIn')}: {getBuyInDisplay(room)}</Typography>
                                            <Typography variant="body2" sx={{ color: playerStatusColor }}>
                                                {playerSatCount} / {maxPlayers}
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                    <CardActions sx={{ justifyContent: 'center', padding: '8px 16px' }}>
                                        <Button 
                                            size="small" 
                                            variant="contained"
                                            fullWidth
                                            sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                                            onClick={() => joinRoom(room.roomId)}
                                        >
                                            {t('Tournament:actions.openTable')}
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            </Paper>
        </Box>
    );
};

export default MobileTournamentRoomsLobby; 
